import { debounce } from 'lodash';
import { TriangleAlert } from 'lucide-react';

import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AgreementConditionEnum, type IAgreementUpdateParams } from 'src/graphql/schema';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { releaseEventsOptionsRich as options } from '../model';

type Props = {
  title: string;
  description: string;
  onUpdate: (agreementParams: IAgreementUpdateParams) => void;
  conditions?: AgreementConditionEnum[];
  conditionsOther?: string;
  onOtherChange: (value: string) => void;
  isOtherInvalid?: boolean;
};

const OTHER_ERROR_ID = 'conditionsOther-error';

const AgreementConditionsSettings = ({
  title,
  description,
  onUpdate,
  conditions = [],
  conditionsOther,
  onOtherChange,
  isOtherInvalid = false,
}: Props) => {
  const onConditionsChange = (conditions: AgreementConditionEnum[]) => onUpdate({ conditions });

  const onConditionsOtherChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => onOtherChange(event.target.value),
    500,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">{title}</CardTitle>
        <CardDescription className="whitespace-pre-line">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ToggleGroupCard
          type="multiple"
          className="flex-col w-full"
          onValueChange={onConditionsChange}
          value={conditions}
        >
          {options.map(({ title, value, description, icon }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={title} description={description} />
          ))}
        </ToggleGroupCard>

        {conditions.includes(AgreementConditionEnum.Other) && (
          <div className="mt-6">
            <Label htmlFor="conditionsOther">List your custom events:</Label>
            <Input
              id="conditionsOther"
              placeholder="e.g. Loss of key personnel"
              onChange={onConditionsOtherChange}
              defaultValue={conditionsOther || ''}
              aria-invalid={isOtherInvalid}
              aria-describedby={isOtherInvalid ? OTHER_ERROR_ID : undefined}
            />
            {isOtherInvalid && (
              <p id={OTHER_ERROR_ID} className="mt-2 flex items-start gap-1.25 text-xs text-destructive">
                <TriangleAlert className="size-3.5 shrink-0" />
                List your custom events.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgreementConditionsSettings;
